import React from 'react';
import { cn } from 'lib/utils';
import { QuestionStatusLine } from './QuestionStatusLine';

interface TextListProps {
  textList: string[];
  setTextList: (value: string[]) => void;
  labels?: string[];
  groupLabel?: string;
  disabled?: boolean;
}

const MIN_ROW_HEIGHT = 48;

/** A one-line text area that grows to fit what is written. */
const GrowingField = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }
>(({ value, className, ...props }, forwardedRef) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useLayoutEffect(() => {
    const element = innerRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.max(MIN_ROW_HEIGHT, element.scrollHeight + 2)}px`;
  }, [value]);

  return (
    <textarea
      {...props}
      rows={1}
      value={value}
      ref={(element) => {
        innerRef.current = element;
        if (typeof forwardedRef === 'function') forwardedRef(element);
        else if (forwardedRef) forwardedRef.current = element;
      }}
      className={cn(
        'block w-full resize-none overflow-hidden rounded-xl border px-4 py-2.5 text-base leading-6 text-foundation-base-darkbase-dark-500 shadow-sm transition-colors placeholder:text-foundation-base-darkbase-dark-300 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    />
  );
});
GrowingField.displayName = 'GrowingField';

export const TextList: React.FC<TextListProps> = ({
  textList,
  setTextList,
  labels,
  groupLabel,
  disabled = false,
}) => {
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);
  const fieldsRef = React.useRef<Array<HTMLTextAreaElement | null>>([]);
  const generatedId = React.useId().replace(/:/g, '');
  const resolvedGroupLabel = groupLabel?.trim() || 'Responses';
  const hasLabels = (labels ?? []).some((label) => typeof label === 'string' && label.trim().length > 0);
  const filledCount = textList.filter((value) => value.trim().length > 0).length;
  const total = textList.length;

  const onChange = (newValue: string, idx: number) => {
    const next = [...textList];
    next[idx] = newValue;
    setTextList(next);
  };

  // Enter moves to the next line; Shift+Enter still starts a new line inside an answer.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    fieldsRef.current[index + 1]?.focus();
  };

  const statusText = (() => {
    const noun = hasLabels ? 'lines filled' : 'added';
    if (filledCount === total) return `All ${total} ${noun}.`;
    return `${filledCount} of ${total} ${noun}. Fill every line to continue.`;
  })();

  return (
    <fieldset className="flex min-w-0 flex-col items-start gap-3 relative self-stretch w-full flex-[0_0_auto] border-0 p-0 m-0">
      <legend className="sr-only">{resolvedGroupLabel}</legend>
      {textList.map((value, index) => {
        const isFocused = focusedIndex === index;
        const isFilled = value.trim().length > 0;
        const suppliedLabel = typeof labels?.[index] === 'string' ? labels[index].trim() : '';
        const accessibleLabel = suppliedLabel || `Response ${index + 1}`;
        const inputId = `text-list-${generatedId}-${index}`;

        return (
          <div
            key={index + 1}
            className="flex flex-col items-start gap-4 relative self-stretch w-full flex-[0_0_auto] translate-y-[-1rem] animate-fade-in opacity-0"
            style={
              {
                '--animation-delay': `${100 + index * 100}ms`,
              } as React.CSSProperties
            }
          >
            <div className="flex items-start gap-3 w-full">
              <div
                data-testid={`text-list-number-${index}`}
                aria-hidden="true"
                className={cn(
                  'mt-2.5 shrink-0 w-7 h-7 rounded-full grid place-items-center text-xs font-semibold transition-colors',
                  suppliedLabel && 'mt-[2.125rem]',
                  isFilled
                    ? 'bg-foundation-base-darkbase-dark-500 text-white'
                    : cn('border bg-white text-foundation-base-darkbase-dark-300', isFocused ? 'border-foundation-base-darkbase-dark-300' : 'border-foundation-base-darkbase-dark-100'),
                )}
              >
                <span className="leading-none">{isFilled ? '✓' : index + 1}</span>
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <label
                  htmlFor={inputId}
                  className={suppliedLabel ? 'text-sm font-semibold text-foundation-base-darkbase-dark-500' : 'sr-only'}
                >
                  {accessibleLabel}
                </label>
                <GrowingField
                  disabled={disabled}
                  id={inputId}
                  data-testid={`text-list-input-${index}`}
                  ref={(element) => {
                    fieldsRef.current[index] = element;
                  }}
                  value={value}
                  onChange={(e) => onChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={() =>
                    setFocusedIndex((prev) => (prev === index ? null : prev))
                  }
                  className={`${isFocused ? 'border-foundation-base-darkbase-dark-300 bg-white' : 'border-foundation-base-darkbase-dark-100 bg-white/80'} focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2`}
                />
              </div>
            </div>
          </div>
        );
      })}
      {total > 1 ? (
        <QuestionStatusLine ready={filledCount === total} className="mt-1">
          {statusText}
        </QuestionStatusLine>
      ) : null}
    </fieldset>
  );
};
